/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { InstallMethod, Technology } from '../../constants/installation';
import { CKEDITOR5_VERSION } from '@ckeditor/ckeditor5-metadata';

import angularPackageJson from '../../assets/integrations/angular/package.json?raw';
import jsCdnPackageJson from '../../assets/integrations/jsCdn/package.json?raw';
import jsNpmPackageJson from '../../assets/integrations/jsNpm/package.json?raw';
import jsZipPackageJson from '../../assets/integrations/jsZip/package.json?raw';
import reactPackageJson from '../../assets/integrations/react/package.json?raw';
import vuePackageJson from '../../assets/integrations/vue/package.json?raw';

export function getExportPackageJson( technology: Technology, installMethod: InstallMethod, hasPremiumPlugins: boolean ): string {
	let packageJson: { devDependencies: Record<string, string> };

	if ( installMethod === 'npm' && technology === 'js' ) {
		packageJson = JSON.parse( jsNpmPackageJson );
	}

	if ( installMethod === 'cdn' && technology === 'js' ) {
		packageJson = JSON.parse( jsCdnPackageJson );
	}

	if ( installMethod === 'zip' && technology === 'js' ) {
		packageJson = JSON.parse( jsZipPackageJson );
	}

	if ( technology === 'react' ) {
		packageJson = JSON.parse( reactPackageJson );
	}

	if ( technology === 'vue' ) {
		packageJson = JSON.parse( vuePackageJson );
	}

	if ( technology === 'angular' ) {
		packageJson = JSON.parse( angularPackageJson );
	}

	// Regardless of the installation method, Angular needs the `ckeditor5` and `ckeditor5-premium-features` packages to access the typings.
	if ( installMethod === 'npm' || technology === 'angular' ) {
		packageJson!.devDependencies.ckeditor5 = CKEDITOR5_VERSION;

		if ( hasPremiumPlugins ) {
			packageJson!.devDependencies[ 'ckeditor5-premium-features' ] = CKEDITOR5_VERSION;
		}
	}

	packageJson!.devDependencies = Object.keys( packageJson!.devDependencies )
		.sort()
		.reduce<Record<string, string>>( ( output, key ) => {
			output[ key ] = packageJson.devDependencies[ key ];

			return output;
		}, {} );

	return JSON.stringify( packageJson!, null, '\t' );
}
